import { useMemo, useCallback } from 'react';

export const useAccessControl = (currentUserData, currentUserAdvertisers, invitedUserData) => {
  // Determine if current user can modify specific advertiser
  const canModifyAdvertiser = useCallback(
    (advertiserName, timezone) => {
      if (!currentUserData?.role) return false;

      if (currentUserData.role === 'admin') {
        // Admin: Can modify everything
        return true;
      }

      if (currentUserData.role === 'internal') {
        // Internal: Can only modify advertisers from their own list
        const currentUserAdvertisersList = [
          ...(currentUserAdvertisers?.advertisers_utc || []),
          ...(currentUserAdvertisers?.advertisers_non_utc || []),
          ...(currentUserAdvertisers?.advertisers_ctv || []),
          ...(currentUserAdvertisers?.advertisers_gtv || [])
        ];

        return currentUserAdvertisersList.some((adv) => adv.name === advertiserName || adv.advertiser_name === advertiserName);
      }

      return false;
    },
    [currentUserData?.role, currentUserAdvertisers]
  );

  // Determine if current user can modify specific organization
  const canModifyOrganization = useCallback(
    (orgName) => {
      if (!currentUserData?.role) return false;

      if (currentUserData.role === 'admin') {
        // Admin: Can modify everything
        return true;
      }

      if (currentUserData.role === 'internal') {
        // Internal: Can only modify organizations from their own org_utc
        // User A (internal) can modify orgs 4,5,6 (shared) and add 1,2,3 (their own)
        // But cannot modify orgs 7,8 (exclusive to user B)
        return currentUserData.org_utc?.includes(orgName) || false;
      }

      return false;
    },
    [currentUserData?.role, currentUserData?.org_utc]
  );

  // Get available advertisers for current user based on role
  const getAvailableAdvertisers = useMemo(() => {
    if (currentUserData?.role === 'admin') {
      // Admin: Can see all advertisers
      return {
        utc: currentUserAdvertisers?.advertisers_utc || [],
        jst: currentUserAdvertisers?.advertisers_non_utc || []
      };
    }

    if (currentUserData?.role === 'internal') {
      // Internal: Can only see their own advertisers
      return {
        utc: currentUserAdvertisers?.advertisers_utc || [],
        jst: currentUserAdvertisers?.advertisers_non_utc || []
      };
    }

    return { utc: [], jst: [] };
  }, [currentUserData?.role, currentUserAdvertisers]);

  // Check if advertiser is already in current access
  const isAdvertiserInCurrentAccess = useCallback(
    (advertiserName) => {
      if (!invitedUserData) return false;

      const inUtc = invitedUserData.advertisers_utc?.some((adv) => adv.name === advertiserName);
      const inJst = invitedUserData.advertisers_non_utc?.some((adv) => adv.name === advertiserName);
      const inCtv = invitedUserData.advertisers_ctv?.some((adv) => adv.name === advertiserName);
      const inGtv = invitedUserData.advertisers_gtv?.some((adv) => adv.name === advertiserName);

      return inUtc || inJst || inCtv || inGtv;
    },
    [invitedUserData]
  );

  // Get filtered available advertisers (excluding those already in current access)
  const getFilteredAvailableAdvertisers = useMemo(() => {
    const available = getAvailableAdvertisers;

    return {
      utc: available.utc.filter((adv) => !isAdvertiserInCurrentAccess(adv.name)),
      jst: available.jst.filter((adv) => !isAdvertiserInCurrentAccess(adv.name))
    };
  }, [getAvailableAdvertisers, isAdvertiserInCurrentAccess]);

  return {
    canModifyAdvertiser,
    canModifyOrganization,
    getAvailableAdvertisers,
    getFilteredAvailableAdvertisers,
    isAdvertiserInCurrentAccess
  };
};
